import React from "react";

/**
 * Smart image wrapper that:
 *   1. Serves the WebP twin (from /boxes/webp/, /products/webp/,
 *      /media/webp/) with a JPG fallback via <picture>. WebPs are 50-70%
 *      smaller than the source JPGs, transparent to users.
 *   2. Sept 2026 — for product images ONLY, ships responsive srcSet with
 *      -sm (400px) and -md (800px) variants (see scripts/gen_webps.py).
 *      Mobile grid cards get a 15-30 KB image instead of the 60-140 KB
 *      full-fidelity twin. Drops /build-your-own image bandwidth ~70%.
 *   3. Forces explicit width/height so the browser reserves layout
 *      space before the image arrives — eliminates CLS.
 *   4. Below-fold defaults: loading="lazy" + decoding="async". Callers
 *      opt-in to eager loading for the LCP with `priority`.
 *
 * Usage:
 *   <Img src="/products/beef-mince.jpg" alt="Beef Mince" width={600} height={600} />
 */
function toWebp(src) {
  if (!src || typeof src !== "string") return null;
  if (!/^\/(boxes|products|media)\/.+\.jpg$/i.test(src)) return null;
  return src.replace(/^\/(boxes|products|media)\/(.+)\.jpg$/i, "/$1/webp/$2.webp");
}

/** Return {sm, md, smW, mdW} WebP paths for product OR hero (`/hiw/`)
 *  images, else null. `smW`/`mdW` are the widths of each variant so
 *  the `<source srcSet>` descriptors match reality — mixing up 800w
 *  and 400w descriptors would let the browser pick the wrong file for
 *  the viewport. Sept 2026 — added /hiw/* support so the LCP-critical
 *  hero images on /build-your-own + /how-it-works get the responsive
 *  pipeline. */
function toWebpSrcSet(src) {
  if (!src || typeof src !== "string") return null;
  const productMatch = src.match(/^\/products\/(.+)\.jpg$/i);
  if (productMatch) {
    const stem = productMatch[1];
    return {
      sm: `/products/webp/${stem}-sm.webp`, smW: 400,
      md: `/products/webp/${stem}-md.webp`, mdW: 800,
    };
  }
  // Hero images live in /hiw/. `butcher-box.jpg` is the only file
  // there with a -sm variant today; fall through to plain <picture>
  // if a -sm doesn't exist so we don't 404 on a preload.
  const hiwMatch = src.match(/^\/hiw\/(.+)\.jpg$/i);
  if (hiwMatch) {
    const stem = hiwMatch[1];
    return {
      sm: `/hiw/webp/${stem}-sm.webp`, smW: 800,
      md: `/hiw/webp/${stem}.webp`,    mdW: 1200,
    };
  }
  return null;
}

export default function Img({
  src,
  alt,
  width,
  height,
  className,
  priority = false,
  sizes,
  onError,
  ...rest
}) {
  const webp = toWebp(src);
  const srcset = toWebpSrcSet(src);
  const loading = priority ? "eager" : "lazy";
  const fetchPriority = priority ? "high" : "auto";
  // Sensible default `sizes` for the product grid: 2 cols under 768px,
  // 3 cols under 1280px, 4 cols under 1536px, 5 cols above.
  const sizesAttr =
    sizes ||
    (srcset
      ? "(max-width: 768px) 50vw, (max-width: 1280px) 33vw, (max-width: 1536px) 25vw, 20vw"
      : undefined);

  const imgProps = {
    src,
    alt: alt || "",
    width,
    height,
    className,
    loading,
    decoding: "async",
    fetchPriority: fetchPriority,
    onError,
    ...rest,
  };

  if (!webp && !srcset) {
    return <img {...imgProps} />;
  }

  // Product images get srcset for responsive delivery
  if (srcset) {
    return (
      <picture>
        <source
          type="image/webp"
          srcSet={`${srcset.sm} ${srcset.smW}w, ${srcset.md} ${srcset.mdW}w`}
          sizes={sizesAttr}
        />
        {/* Legacy WebP fallback for browsers that don't parse srcset */}
        <source type="image/webp" srcSet={webp} />
        <img {...imgProps} />
      </picture>
    );
  }

  // Non-product (boxes/media) — single WebP twin
  return (
    <picture>
      <source srcSet={webp} type="image/webp" />
      <img {...imgProps} />
    </picture>
  );
}
